import {View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {type AccountType} from '#/state/classrooms/account'
import {
  CLASSROOM_POST_SEATS,
  type ClassroomPostAudience,
  EVERYONE_MAY_READ,
  isTheWholeClassroom,
  mayAnyoneRead,
  THE_WHOLE_CLASSROOM,
} from '#/state/classrooms/types'
import {atoms as a, useTheme} from '#/alf'
import {ownerNoun} from '#/components/classrooms/ownerNoun'
import {
  postSeatName,
  postSeatWord,
} from '#/components/classrooms/postAudienceNames'
import * as Toggle from '#/components/forms/Toggle'
import {Text} from '#/components/Typography'

/**
 * Who a post written in a classroom is for.
 *
 * The one question a classroom's composer asks that no other composer can, and it
 * is asked from the pill beside *Anyone can interact* — the same row of pills, one
 * question per pill — with a dialog of its own (`PostAudienceDialog`). It is not
 * folded into *Post interaction settings*: *who may reply* is a standing
 * preference of an account, and *who may see this post* is a decision about one
 * post, so each has a door of its own even though they share the row and the save.
 *
 * ## Two kinds of answer, drawn as two kinds of control
 *
 * Two of the answers are *everybody*: the world, and the whole room. Neither is a
 * list of people, both are the widest thing the question can mean, and a person
 * picking one is not picking anybody in particular — so they are drawn as the two
 * **radios** (`EVERYONE_MAY_READ`, `THE_WHOLE_CLASSROOM`), which is how the app
 * says *one of these two, or neither*. The three seats below them are the narrow
 * answers, and narrow answers combine: a note about a trip home is for the parents
 * *and* the students, and a form with one answer in it could not write that note —
 * so they are drawn as **checkboxes** and any of them may be ticked with any
 * other.
 *
 * Ticking all three is the whole room, which is what the *Only this classroom*
 * radio already says, so the two are drawn as the same answer: the seats are shown
 * unticked the moment the audience is every seat, and the radio carries it. That
 * is not a hidden state — it is the same audience, said the shorter way — and it
 * is what keeps a person from having to work out whether three ticks and the radio
 * mean the same thing.
 *
 * ## What cannot be chosen
 *
 * The seats can never be emptied by unticking them: a post has to be for somebody,
 * and *for everybody* is a thing the author says on purpose (a radio), not
 * something that happens by unticking the last box. Unticking the last ticked seat
 * therefore leaves the answer where it was.
 *
 * Every room offers both *Public* and *Only this classroom* (spec 11.3): the
 * author picks per post, a private room starting on the room and a public room
 * on the world (`defaultPostAudience`). The answer becomes the room entry's
 * `visibility` (`postVisibility`), and the seats narrow a room-only post.
 *
 * The school's own account is in every audience, teacher included
 * (`audienceIncludes`): it runs the room, hands out the code and can end it, and a
 * school reading a room it cannot see is a school that cannot maintain one.
 */
export function PostAudienceOptions({
  ownerAccountType,
  audience,
  onChange,
}: {
  /** The kind of account that runs the room, for naming it (`ownerNoun`). */
  ownerAccountType: AccountType | null
  audience: ClassroomPostAudience
  onChange: (audience: ClassroomPostAudience) => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const owner = ownerNoun(l, ownerAccountType)

  /*
   * Which radio is filled, and which boxes are ticked.
   *
   * The two are read off one audience and never both speak for it: the world and
   * the whole room are the radios' answers, and the seats are drawn only when the
   * author named some but not all of them (the division is `CLASSROOM_POST_SEATS`
   * — a *proper* subset).
   */
  const radioRows = mayAnyoneRead(audience)
    ? ['public']
    : isTheWholeClassroom(audience)
      ? ['members']
      : []
  const tickedSeats = isTheWholeClassroom(audience) ? [] : [...audience]

  /*
   * The three seats, in the order the room lists its members: the people who
   * teach, then the people who brought somebody, then the people here to learn.
   * Spelled out as rows so the three cannot drift apart — they are one question
   * asked three times, and `audienceIncludes` answers each of them the same way.
   *
   * `teachers` and not `admin`: the school is in every audience, so the row that
   * says *teachers* is about the people who teach here, which is what the label
   * has to mean to a person who is one.
   */
  const seats: {
    seat: (typeof CLASSROOM_POST_SEATS)[number]
    name: string
    description: string
  }[] = [
    {
      seat: 'teachers',
      name: postSeatName(l, 'teachers'),
      description: l`Only the teachers in this classroom, and ${owner}.`,
    },
    {
      seat: 'parents',
      name: postSeatName(l, 'parents'),
      description: l`Only the parents in this classroom, and ${owner}.`,
    },
    {
      seat: 'students',
      name: postSeatName(l, 'students'),
      description: l`Only the students in this classroom, and ${owner}.`,
    },
  ]

  return (
    <View testID="postAudienceOptions" style={[a.gap_md]}>
      <Text style={[a.text_md, a.font_medium]}>{l`Audience`}</Text>
      {/*
        Everybody, and everybody in the room: one of the two, or neither. A radio
        group rather than two rows of a larger list because neither answer names
        anybody, so there is nothing for them to combine with.
      */}
      <Toggle.Group
        label={l`Public, or only this classroom`}
        type="radio"
        values={radioRows}
        onChange={rows => {
          onChange(
            rows[0] === 'public' ? EVERYONE_MAY_READ : THE_WHOLE_CLASSROOM,
          )
        }}>
        <Toggle.PanelGroup>
          <Toggle.Item
            name="public"
            type="radio"
            hitSlop={0}
            label={l`Public`}>
            {({selected}) => (
              <Toggle.Panel active={selected}>
                <Toggle.Radio />
                <View style={[a.flex_1, a.gap_2xs]}>
                  <Toggle.PanelText>{l`Public`}</Toggle.PanelText>
                  <Text
                    style={[
                      a.text_sm,
                      a.leading_snug,
                      t.atoms.text_contrast_medium,
                    ]}>
                    {l`Anyone can read it, here and on your profile.`}
                  </Text>
                </View>
              </Toggle.Panel>
            )}
          </Toggle.Item>

          <Toggle.Item
            name="members"
            type="radio"
            hitSlop={0}
            label={l`Only this classroom`}>
            {({selected}) => (
              <Toggle.Panel active={selected}>
                <Toggle.Radio />
                <View style={[a.flex_1, a.gap_2xs]}>
                  <Toggle.PanelText>{l`Only this classroom`}</Toggle.PanelText>
                  <Text
                    style={[
                      a.text_sm,
                      a.leading_snug,
                      t.atoms.text_contrast_medium,
                    ]}>
                    {l`Only the people in this classroom, whatever their role. Nobody outside can read it.`}
                  </Text>
                </View>
              </Toggle.Panel>
            )}
          </Toggle.Item>
        </Toggle.PanelGroup>
      </Toggle.Group>
      {/*
        The narrow answers, which combine. Ticking the last of them is refused
        rather than obeyed (`onChange` below): the seats are for naming *some* of
        the room, and the answer that means everybody is the radio above.

        The radio rows are deliberately *not* in this group. They are not seats and
        they are not ticked — one of them is filled, and a filled radio sitting in
        a group of checkboxes would be the app saying two things at once about the
        one question it is asking.
      */}
      <Toggle.Group
        label={l`Or only some of the people in this classroom`}
        type="checkbox"
        values={tickedSeats}
        onChange={rows => {
          const seated = CLASSROOM_POST_SEATS.filter(seat =>
            rows.includes(seat),
          )
          /*
           * Nothing left ticked means the last one was just unticked, and a post
           * has to be for somebody. Left where it was, rather than widened to the
           * world or narrowed to nobody: the person who unticked it is one tap
           * from *Only this classroom* and one from *Public*, and neither
           * may happen to them unasked.
           */
          if (seated.length === 0) return
          onChange(seated)
        }}>
        <View style={[a.gap_2xs]}>
          <Toggle.PanelGroup>
            {seats.map(seat => (
              <Toggle.Item
                key={seat.seat}
                name={seat.seat}
                type="checkbox"
                hitSlop={0}
                label={seat.name}>
                {({selected}) => (
                  <Toggle.Panel active={selected}>
                    <Toggle.Checkbox />
                    <View style={[a.flex_1, a.gap_2xs]}>
                      <Toggle.PanelText>{seat.name}</Toggle.PanelText>
                      <Text
                        style={[
                          a.text_sm,
                          a.leading_snug,
                          t.atoms.text_contrast_medium,
                        ]}>
                        {seat.description}
                      </Text>
                    </View>
                  </Toggle.Panel>
                )}
              </Toggle.Item>
            ))}
          </Toggle.PanelGroup>

          {/* Said out loud, because a checkbox list can also mean *one of*. */}
          <Text
            testID="postAudienceSeatsHint"
            style={[
              a.pl_md,
              a.text_sm,
              a.leading_snug,
              t.atoms.text_contrast_medium,
            ]}>
            <Trans>
              Tick more than one seat to write to them at the same time — a post
              can be for the {postSeatWord(l, 'parents')} and the{' '}
              {postSeatWord(l, 'students')} at once.
            </Trans>
          </Text>
        </View>
      </Toggle.Group>
    </View>
  )
}
